import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { Heart, CheckCircle, FileText, MapPin, Activity } from "lucide-react";

export default function VetProBono() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  const load = () => axios.get(`${API}/partner/vet/pro-bono-cases`).then(r => setItems(r.data.items)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6" data-testid="vet-probono-page">
      <header>
        <div className="text-xs uppercase tracking-[0.2em] text-brand font-bold">My Clinic · Goodwill</div>
        <h1 className="font-heading text-3xl font-extrabold mt-1">Pro-bono Rescue Cases</h1>
        <p className="text-ink-soft text-sm">NGOs trust you with their rescues. Each treatment counts towards your "Pet Hero" badge.</p>
      </header>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map(c => (
          <article key={c.rescue_case_id} data-testid={`probono-${c.rescue_case_id}`} onClick={() => setActive(c)} className="cursor-pointer bg-surface border border-line rounded-3xl overflow-hidden shadow-soft hover:-translate-y-1 transition">
            <img src={c.photo} alt={c.pet_name} className="w-full h-32 object-cover"/>
            <div className="p-4">
              <div className="flex items-center justify-between">
                <h3 className="font-heading text-lg font-extrabold">{c.pet_name}</h3>
                <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 ${c.vet_status === "treated" ? "bg-brand-green text-white" : c.vet_status === "accepted" ? "bg-brand-blue text-white" : "bg-sun text-ink"}`}>
                  {c.vet_status || "pending"}
                </span>
              </div>
              <div className="text-xs text-ink-soft mt-1">{c.species} · {c.breed}</div>
              <div className="text-xs text-ink-soft mt-2 inline-flex items-center gap-1"><MapPin className="w-3 h-3"/>{c.intake_location}</div>
              <div className="text-xs text-ink-soft mt-1">via <span className="font-bold">{c.ngo_name}</span></div>
            </div>
          </article>
        ))}
      </div>
      {items.length === 0 && (
        <div className="bg-surface border border-dashed border-line rounded-3xl p-10 text-center text-ink-soft">No pro-bono cases assigned yet. NGOs will reach out when a rescue needs your help.</div>
      )}
      {active && <CaseModal c={active} onClose={() => { setActive(null); load(); }}/>}
    </div>
  );
}

function CaseModal({ c, onClose }) {
  const [treatment, setTreatment] = useState(c.treatment_notes || "");
  const accept = async () => { await axios.post(`${API}/partner/vet/pro-bono-cases/${c.rescue_case_id}/accept`); onClose(); };
  const complete = async () => { await axios.post(`${API}/partner/vet/pro-bono-cases/${c.rescue_case_id}/complete`, { treatment }); onClose(); };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-surface rounded-3xl w-full max-w-lg max-h-[90vh] overflow-y-auto" data-testid="probono-modal">
        <img src={c.photo} alt={c.pet_name} className="w-full h-44 object-cover rounded-t-3xl"/>
        <div className="p-6">
          <h3 className="font-heading text-2xl font-extrabold">{c.pet_name}</h3>
          <p className="text-sm text-ink-soft mt-1">From {c.ngo_name} · {c.intake_location}</p>
          <p className="text-sm mt-3">{c.description}</p>
          {c.timeline?.length > 0 && (
            <div className="mt-4 bg-surface-muted/50 rounded-2xl p-3">
              <div className="text-xs uppercase tracking-wider font-bold text-ink-soft mb-2 inline-flex items-center gap-1"><Activity className="w-3 h-3"/>Journey</div>
              <div className="space-y-1.5 text-xs">
                {c.timeline.slice().reverse().slice(0, 5).map((t, i) => (
                  <div key={i}><span className="font-bold">{t.note}</span> <span className="text-ink-soft">— {new Date(t.at).toLocaleDateString()}</span></div>
                ))}
              </div>
            </div>
          )}
          {c.vet_status !== "treated" && (
            <label className="block mt-4">
              <span className="text-xs uppercase tracking-wider font-bold text-ink-soft">Treatment notes</span>
              <textarea value={treatment} onChange={e => setTreatment(e.target.value)} rows={3} className="w-full mt-1 bg-surface-muted border border-line rounded-2xl p-3 outline-none focus:border-brand"/>
            </label>
          )}
          <div className="mt-4 flex gap-2">
            {(c.vet_status === null || c.vet_status === "pending") && (
              <button onClick={accept} data-testid="probono-accept" className="flex-1 bg-brand-blue text-white rounded-full py-2.5 font-bold inline-flex items-center justify-center gap-1.5">
                <Heart className="w-4 h-4"/> Accept case
              </button>
            )}
            {c.vet_status === "accepted" && (
              <button onClick={complete} data-testid="probono-complete" className="flex-1 bg-brand-green text-white rounded-full py-2.5 font-bold inline-flex items-center justify-center gap-1.5">
                <CheckCircle className="w-4 h-4"/> Mark treated
              </button>
            )}
            <button onClick={onClose} className="px-4 bg-surface-muted rounded-full font-bold">Close</button>
          </div>
        </div>
      </div>
    </div>
  );
}
